import { cn } from '@commenda/utils';
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { ChartColorHelper } from './chartColors';
import {
  AXIS_TICK,
  BOUNDARY_LABEL_PROPS,
  BOUNDARY_LINE_CLASS,
  ChartHelper,
  DEFAULT_VALUE_FORMATTER,
  GRID_CLASS,
  PRELIMINARY_DASH,
} from './chartHelpers';
import type { IBaseChartProps } from './chartTypes';

export interface ILineChartProps extends IBaseChartProps {
  connectNulls?: boolean;
}

export const LineChart = ({
  data,
  index,
  categories,
  colors,
  valueFormatter = DEFAULT_VALUE_FORMATTER,
  connectNulls = false,
  showLegend = true,
  showGridLines = true,
  showXAxis = true,
  sparseXLabels = false,
  showYAxis = true,
  yAxisWidth = 56,
  autoMinValue = false,
  minValue,
  maxValue,
  customTooltip,
  className,
}: ILineChartProps) => {
  const categoryColors = ChartColorHelper.categoryColors(categories, colors);
  const Tip = customTooltip ?? ChartTooltip;
  const split = ChartHelper.splitPreliminary(data, index, categories);

  return (
    <div className={cn('flex h-80 w-full flex-col', className)}>
      {showLegend ? (
        <ChartLegend
          className="mb-sm"
          items={categories.map((c) => ({
            name: c,
            color: categoryColors.get(c) ?? 'gray',
            shape: 'line',
          }))}
        />
      ) : null}
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart
            data={split.data}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
          >
            {showGridLines ? (
              <CartesianGrid
                className={GRID_CLASS}
                horizontal
                vertical={false}
              />
            ) : null}
            <XAxis
              dataKey={index}
              hide={!showXAxis}
              tickLine={false}
              axisLine={false}
              interval={sparseXLabels ? 0 : 'equidistantPreserveStart'}
              ticks={
                sparseXLabels ? ChartHelper.sparseTicks(data, index) : undefined
              }
              minTickGap={8}
              tick={AXIS_TICK}
              padding={{ left: 8, right: 8 }}
            />
            <YAxis
              width={yAxisWidth}
              hide={!showYAxis}
              tickLine={false}
              axisLine={false}
              domain={ChartHelper.yAxisDomain(autoMinValue, minValue, maxValue)}
              tick={AXIS_TICK}
              tickFormatter={valueFormatter}
              allowDecimals
            />
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              isAnimationActive={false}
              cursor={{ className: 'stroke-border-primary' }}
              content={({ active, payload, label }) => (
                <Tip
                  active={active}
                  label={ChartHelper.tooltipLabel(label)}
                  payload={ChartHelper.tooltipEntries(payload, categoryColors)}
                  valueFormatter={valueFormatter}
                  preliminary={ChartHelper.tooltipPreliminary(payload)}
                />
              )}
            />
            {split.boundaryLabel !== null && (
              <ReferenceLine
                x={split.boundaryLabel}
                className={BOUNDARY_LINE_CLASS}
                stroke=""
                label={BOUNDARY_LABEL_PROPS}
              />
            )}
            {categories.map((category) => (
              <Line
                key={category}
                name={category}
                dataKey={category}
                type="linear"
                connectNulls={connectNulls}
                isAnimationActive={false}
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 4,
                  strokeWidth: 0,
                  className: ChartColorHelper.className(
                    categoryColors.get(category) ?? 'gray',
                    'fill'
                  ),
                }}
                className={ChartColorHelper.className(
                  categoryColors.get(category) ?? 'gray',
                  'stroke'
                )}
                stroke=""
              />
            ))}
            {[...split.preliminaryKeys].map(([category, key]) => (
              <Line
                key={key}
                name={key}
                dataKey={key}
                type="linear"
                connectNulls={connectNulls}
                isAnimationActive={false}
                strokeWidth={2}
                strokeDasharray={PRELIMINARY_DASH}
                dot={false}
                activeDot={false}
                legendType="none"
                className={ChartColorHelper.className(
                  categoryColors.get(category) ?? 'gray',
                  'stroke'
                )}
                stroke=""
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
